<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import { useMobileMenu } from '@/composables/useMobileMenu'
import { Sun, Moon, AlertTriangle, MessageCircle, Menu, Bell } from 'lucide-vue-next'
import CalendarHoverWidget from './CalendarHoverWidget.vue'
import TodosFlyout from './TodosFlyout.vue'
import HelpFlyout from './HelpFlyout.vue'
import { useSocket } from '@/composables/useSocket'
import { useChatStore } from '@/stores/chat'
import { useNotificationCenterStore } from '@/stores/notificationCenter'

const route = useRoute()
const themeStore = useThemeStore()
const authStore = useAuthStore()
const chatStore = useChatStore()
const { connectionState } = useSocket()

const notifStore = useNotificationCenterStore()
const { isMobile, toggleMenu } = useMobileMenu()
const canViewChat = computed(() => authStore.hasPermission('chat.view'))

const pageTitle = computed(() => {
  const name = route.meta.title || route.name
  if (typeof name === 'string') {
    // Add space before capital letters (converts "ProjectDetail" to "Project Detail")
    return name.replace(/([a-z])([A-Z])/g, '$1 $2')
  }
  return 'Dashboard'
})
const isDark = computed(() => themeStore.theme === 'dark')
</script>

<template>
  <header class="sticky top-0 z-30 bg-surface-900/80 backdrop-blur-md border-b border-surface-800">
    <div class="flex items-center justify-between px-4 py-3 md:px-6 md:py-4">
      <div class="flex items-center gap-3">
        <!-- Hamburger menu (mobile only) -->
        <button
          v-if="isMobile"
          @click="toggleMenu"
          class="p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors -ml-2"
          title="Menu"
        >
          <Menu class="w-5 h-5" />
        </button>
        <h1 class="text-lg md:text-xl font-semibold text-surface-100 truncate">{{ pageTitle }}</h1>
      </div>
      <div class="flex items-center gap-1.5 md:gap-3">
        <!-- Impersonation Warning Banner -->
        <div v-if="authStore.impersonating" class="hidden md:flex items-center gap-2 px-3 py-1.5 bg-warning-500/20 border border-warning-500/30 rounded-lg">
          <AlertTriangle class="w-4 h-4 text-warning-400" />
          <span class="text-sm text-warning-400">Impersonating {{ authStore.user?.username }}</span>
          <button @click="authStore.stopImpersonation" class="text-xs text-warning-300 hover:text-warning-100 underline ml-2">Stop</button>
        </div>
        <!-- Impersonation indicator (mobile — just icon) -->
        <div v-if="authStore.impersonating" class="md:hidden p-2 bg-warning-500/20 rounded-lg">
          <AlertTriangle class="w-4 h-4 text-warning-400" />
        </div>

        <!-- Real-time Connection Status -->
        <div class="flex items-center gap-1.5 px-2 py-1 rounded-lg" :title="connectionState === 'connected' ? 'Real-time connected' : connectionState === 'connecting' ? 'Reconnecting...' : 'Offline — polling'">
          <span class="relative flex h-2 w-2 flex-shrink-0">
            <span v-if="connectionState === 'connected'" class="h-2 w-2 rounded-full bg-success-500"></span>
            <template v-else-if="connectionState === 'connecting'">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </template>
            <span v-else class="h-2 w-2 rounded-full bg-surface-600"></span>
          </span>
          <span class="hidden md:inline text-xs" :class="connectionState === 'connected' ? 'text-success-500' : connectionState === 'connecting' ? 'text-amber-400' : 'text-surface-500'">
            {{ connectionState === 'connected' ? 'Real-time' : connectionState === 'connecting' ? 'Reconnecting...' : 'Offline' }}
          </span>
        </div>

        <!-- Chat Queue Indicator -->
        <button
          v-if="canViewChat"
          @click="chatStore.toggle()"
          class="relative p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
          title="Live Chat"
        >
          <MessageCircle class="w-5 h-5" />
          <span
            v-if="chatStore.unreadCount > 0"
            class="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold bg-danger-500 text-white rounded-full"
          >
            {{ chatStore.unreadCount > 99 ? '99+' : chatStore.unreadCount }}
          </span>
        </button>

        <!-- Todos Flyout Widget (Staff Only) -->
        <TodosFlyout />

        <!-- Calendar Hover Widget (Staff Only) -->
        <CalendarHoverWidget />

        <!-- Notification Center Bell -->
        <button
          @click="notifStore.toggle()"
          class="relative p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
          title="Notifications"
        >
          <Bell class="w-5 h-5" />
          <span
            v-if="notifStore.unreadCount > 0"
            class="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold bg-primary-500 text-white rounded-full"
          >
            {{ notifStore.unreadCount > 99 ? '99+' : notifStore.unreadCount }}
          </span>
        </button>

        <!-- Help Flyout -->
        <HelpFlyout />

        <!-- Theme Toggle -->
        <button 
          @click="themeStore.toggleTheme" 
          class="p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors" 
          :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <Sun v-if="isDark" class="w-5 h-5" />
          <Moon v-else class="w-5 h-5" />
        </button>
      </div>
    </div>
  </header>
</template>
